import moment from 'moment'
import _ from 'lodash'

export const DATE_FORMAT = {
  LINE_CHART_X_AXIS_DATE: 'MM/DD',
  LINE_CHART_X_AXIS_MONTH: 'M 月',
  UI: 'YYYY/MM/DD',
  API: 'YYYY-MM-DD',
  API_START_DATE: 'YYYY-MM-01',
  DASHBOARD: 'MM/DD HH:mm',
  API_DATETIME: 'YYYY-MM-DDTHH:mm:ssZ',
  API_TIMEZONE_DATETIME: 'YYYY-MM-DDTHH:mm:ss[Z]',
  API_DATETIME_SEC_START: 'YYYY-MM-DDTHH:mm:00[Z]',
  API_DATETIME_SEC_END: 'YYYY-MM-DDTHH:mm:59[Z]',
  API_DATETIME_START: 'YYYY-MM-DDT00:00:00Z',
  API_DATETIME_END: 'YYYY-MM-DDT23:59:59Z',
  CHART_X_AXIS_TIME: 'HH:mm',
  SCHEDULE: 'YYYY-MM-DD HH:mm',
  SCHEDULE_SECONDS: 'YYYY/MM/DD HH:mm:ss'
}

export const TIME_DURATION = {
  DAY: {
    label: '日',
    value: 'day'
  },
  WEEK: {
    label: '週',
    value: 'week'
  },
  MONTH: {
    label: '月',
    value: 'month'
  }
}

/**
 * 取得兩個 Moment 之間的所有 Moment,
 * 包含一開始的 start 與 end
 *
 * @example
 * // return [Moment(1/1), Moment(1/2), Moment(1/3)]
 * getRangeOfDates(moment('2019-01-01'), moment('2019-01-03'), 'days')
 *
 * @example
 * // return [Moment(7/1), Moment(8/1), Moment(9/1), Moment(10/1)]
 * getRangeOfDates(moment('2019-07-03', moment('2019-10-10'), 'months'))
 */
export const getRangeOfDates = (start, end, key) => {
  if (start.isAfter(end)) throw new Error('start must precede end')

  const startPoint = start.startOf(key)
  const ranges = [startPoint]

  const getNext = date =>
    moment(date)
      .add(1, key)
      .startOf(key)

  let next = getNext(startPoint)
  while (!next.isAfter(end)) {
    ranges.push(next)
    next = getNext(next)
  }

  return ranges
}

/**
 * 取得兩 Moment 之間的每一天 Moment
  * 包含一開始的 start 與 end
  * @example
  * // return [Moment(1/1), Moment(1/2), Moment(1/3)]
  * getRangeDaysOfDates(moment('2019-01-01'), moment('2019-01-03'))
 */
export const getRangeDaysOfDates = (start, end) => getRangeOfDates(start, end, 'days')

/**
 * 取得兩個 Moment 之間 API 格式的每一天日期時間區間
 * @example
 * // return [
 * //   '2019-01-01T00:00:00+08:00/2019-01-01T23:59:59+08:00',
 * //   '2019-01-02T00:00:00+08:00/2019-01-02T23:59:59+08:00'
 * // ]
 * getRangeAPIDateTimesOfDates(moment('2019-01-01'), moment('2019-01-02'))
 */
export const getRangeAPIDateTimesOfDates = (start, end) => {
  const rangeAPIDateTimesOfDates = _.map(getRangeDaysOfDates(start, end), day => {
    return _.flow(
      formats => _.map(formats, format => getFormatData(day, format)),
      formatedDates => _.join(formatedDates, '/')
    )([DATE_FORMAT.API_DATETIME, DATE_FORMAT.API_DATETIME_END])
  })
  return rangeAPIDateTimesOfDates
}

/**
 * 取得兩個 Moment 之間的 API 格式的日期時間區間表示法
 * @example
 * // return '2022-11-01T23:00:00+08:00/2022-11-02T01:59:59+08:00'
 * getRangeAPIDateTimes(moment('2022-11-01T23:00:00+08:00'), moment('2022-11-02T01:59:59+08:00'))
 */
export const getRangeAPIDateTimes = (startDateTime, endDateTime) =>
  ([startDateTime, endDateTime])
    .map(datetime => moment(datetime).format(DATE_FORMAT.API_DATETIME))
    .join('/')

export const getFormatData = (momentDate, format) => {
  return momentDate.format(format)
}

/**
 * return 'YYYY-MM-DD'
 */
export const getAPIFormatDate = momentDate => {
  return getFormatData(momentDate, DATE_FORMAT.API)
}

/**
 * return 'YYYY/MM/DD'
 */
export const getUIFormatDate = momentDate => {
  return getFormatData(momentDate, DATE_FORMAT.UI)
}

/**
 * return 'MM 月'
 */
export const getMonthFormatDate = momentDate => {
  return getFormatData(momentDate, DATE_FORMAT.LINE_CHART_X_AXIS_MONTH)
}

/**
 * return 'YYYY-MM-DD/YYYY-MM-DD'
 */
export const getRangeStringForAPI = momentArr => {
  return momentArr
    .map(getAPIFormatDate)
    .join('/')
}

export const DEFAULT_SEPARATOR = ' － '

/**
 * return 'YYYY/MM/DD － YYYY/MM/DD'
 */
export const getRangeStringForUI = (momentArr, separator = DEFAULT_SEPARATOR) => {
  return momentArr
    .map(getUIFormatDate)
    .join(separator)
}

/**
 * return 'MM 月 － MM 月'
 */
export const getMonthRangeStringForUI = (momentArr, separator = DEFAULT_SEPARATOR) => {
  return momentArr
    .map(getMonthFormatDate)
    .join(separator)
}

export const parseApiFormatMoment = v => moment(v, DATE_FORMAT.API)

export const getReducedRange = (firstDate, fullRange = [], reducedBy) => {
  let lastDate
  const [start, end] = fullRange
  if (firstDate.isBefore(start) || firstDate.isAfter(end)) {
    return []
  }
  switch (reducedBy) {
    case TIME_DURATION.MONTH.value: {
      const lastDateOfMonth = moment(firstDate)
        .month(firstDate.month() + 1)
        .date(0)
      lastDate = lastDateOfMonth.isAfter(end) ? end : lastDateOfMonth
      break
    }
    case TIME_DURATION.WEEK.value: {
      const lastDateOfWeek = moment(firstDate).day(6)
      lastDate = lastDateOfWeek.isAfter(end) ? end : lastDateOfWeek
      break
    }
    case TIME_DURATION.DAY.value:
    default: {
      lastDate = firstDate
    }
  }
  return [firstDate, lastDate]
}

export const getIsInRange = (date, range) => {
  if (_.isEmpty(range)) {
    return false
  }
  const [start, end] = range
  return !start.isAfter(date) && !end.isBefore(date)
}

export const getMomentDateTimeFromApiData = datetime => moment(datetime, DATE_FORMAT.API_DATETIME)
  .utc(datetime)

export const getDashboardDateTime = (datetime, format = DATE_FORMAT.DASHBOARD) => getMomentDateTimeFromApiData(datetime)
  .format(format)

export const getRangeAPIDate = (range) => {
  const rangeAPIDate = _.flow(
    formats => _.map(formats, (format, index) => {
      return getFormatData(range[index], format)
    }),
    formatedDates => _.join(formatedDates, '/')
  )([DATE_FORMAT.API_DATETIME_START, DATE_FORMAT.API_DATETIME_END])
  return rangeAPIDate
}

/**
 * convert moment to ISO8601 UTC string without milliseconds
 * @param {moment} momentObj
 * @returns {string} 'YYYY-MM-DDTHH:mm:ssZ'
 */
export const getISO8601UTC = momentObj => momentObj.toISOString().split('.')[0] + 'Z'

/**
 * get range by term and current date
 * 應為前一天起算至 'term' 天前
 * @param {number} term 1, 7, 14, 30 (days)
 * @returns {moment[]} [moment, moment]
 */
export const getRangeByTerm = term => {
  const currentDate = moment()
  return [currentDate.subtract(term, 'days'), moment().subtract(1, 'days')]
}
